import { useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppNavigationType } from '~/helpers/hooks/useAppNavigationType';
import {
  addScrollAnchorExternalSaveListener,
  addScrollAnchorRestoreCancelListener,
  addScrollAnchorTopPinReleaseListener,
  addScrollAnchorTopResetListener,
  appOwnsScrollRestoration,
  notifyScrollAnchorExternalSave,
  suppressScrollAnchorSaves,
  scrollAnchorRestoresAreSuppressed,
  scrollAnchorSavesAreSuppressed
} from '~/helpers/scrollAnchorRestorationCoordinator';
import {
  isScrollDiagnosticsLoggingEnabled,
  shouldRecordScrollDiagnostics,
  recordScrollDiagnostic
} from '~/helpers/scrollAnchorDiagnostics';
import {
  createTopPinNavigationGate,
  createTopPinWindow,
  elementIsEditableField,
  topPinWindowMs,
  type TopPinWindow
} from '~/helpers/scrollTopPin';

// An incidental pointer event (iOS momentum from the swipe-to-reveal-nav, or
// the navigation tap itself) must not cancel an in-flight restore before it has
// actually pinned the saved anchor — otherwise the scroll is stranded at the
// not-yet-settled fallback position. After this grace window we honor user
// input even if the anchor never rendered, so we never fight a deliberate
// scroll for long.
const restoreCancelGraceMs = 900;

// After a programmatic scroll (anchor restore / initial scroll), a scroll
// event that arrives without any user scroll input since that scroll was
// applied is browser-initiated (native history restore, iOS viewport
// settling) — saving it would persist a position the user never chose as the
// new anchor. This window bounds only the DETECTION of such scrolls; one that
// is caught sets a standing taint that keeps blocking saves until user input,
// a hook-applied scroll, or a top reset clears it.
const postRestoreNonUserSaveGuardMs = 3000;

// A scroll observed while saves are suppressed is normally the hook's own
// (setScrollTop's synthetic event and the browser's native echo land exactly
// at the applied position). Deviation beyond this tolerance means something
// else — a browser history restore — moved the scroller inside the
// suppression window, which must taint like any other guarded non-user
// scroll or it would be saved once suppression lapses.
const appliedScrollTopTolerancePx = 2;

interface SavedScrollAnchor {
  anchorKey: string;
  primaryId?: string;
  secondaryId?: string;
  contentKey?: string;
  offset: number;
  scrollTop: number;
}

type InitialScrollPolicy =
  | { type: 'preserve' }
  | { type: 'top' }
  | {
      type: 'element';
      targetRef: RefObject<HTMLElement | null>;
      topOffset?: number;
    };

const savedScrollAnchors: Record<string, SavedScrollAnchor> = {};
const topPinNavigationGate = createTopPinNavigationGate();
const restoreSaveSuppressionDurationMs = 250;
const restoreCancelKeys = new Set([
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'End',
  'Home',
  'PageDown',
  'PageUp',
  ' '
]);
const scrollableOverflowValues = new Set(['auto', 'overlay', 'scroll']);

export function useScrollAnchorRestoration({
  anchorKey,
  containerRef,
  ignoreSavedAnchor = false,
  initialScroll,
  itemsReady
}: {
  anchorKey: string;
  containerRef: RefObject<HTMLElement | null>;
  ignoreSavedAnchor?: boolean;
  initialScroll: InitialScrollPolicy;
  itemsReady: boolean;
}) {
  const initialScrollType = initialScroll.type;
  const initialScrollTargetRef =
    initialScroll.type === 'element' ? initialScroll.targetRef : null;
  const initialScrollTopOffset =
    initialScroll.type === 'element' ? initialScroll.topOffset : undefined;
  const initialScrollAttemptedRef = useRef('');
  const activeIgnoredSavedAnchorKeyRef = useRef('');
  const ignoredSavedAnchorSignaturesRef = useRef<Record<string, string>>({});
  const activeAnchorKeyRef = useRef('');
  const restoreAttemptedRef = useRef('');
  const restoreSettledSignatureRef = useRef('');
  const userCancelledRestoreRef = useRef('');
  const restoreAppliedAtRef = useRef(0);
  const userScrollInputAtRef = useRef(0);
  const nonUserScrollTaintedRef = useRef(false);
  const lastAppliedScrollTopRef = useRef(-1);
  const loggedPendingSkipSignatureRef = useRef('');
  const topPinRef = useRef<TopPinWindow | null>(null);
  const forwardNavTopPinKeyRef = useRef('');
  const arrivedByNavigationRef = useRef(false);
  // Bundled once so the module-level startTopPinWindow can do the same
  // programmatic-scroll bookkeeping as the hook's own scrolls.
  const topPinRefsRef = useRef<TopPinRefs>({
    topPin: topPinRef,
    userScrollInputAt: userScrollInputAtRef,
    restoreAppliedAt: restoreAppliedAtRef,
    nonUserScrollTainted: nonUserScrollTaintedRef,
    lastAppliedScrollTop: lastAppliedScrollTopRef
  });

  if (ignoreSavedAnchor) {
    activeIgnoredSavedAnchorKeyRef.current = anchorKey;
    const ignoredRestoreSignature = getSavedAnchorRestoreSignature(anchorKey);
    if (ignoredRestoreSignature) {
      ignoredSavedAnchorSignaturesRef.current[anchorKey] =
        ignoredRestoreSignature;
    }
  } else if (
    !ignoreSavedAnchor &&
    activeIgnoredSavedAnchorKeyRef.current === anchorKey
  ) {
    activeIgnoredSavedAnchorKeyRef.current = '';
  }

  useLayoutEffect(() => {
    // Per-key state must reset INSIDE the effect, not during render: the
    // previous key's effect cleanups (including its teardown save) run at
    // commit, after render — a render-phase reset would clear the taint and
    // settled signature before that teardown save reads them, letting a
    // browser-chosen offset be saved under the old key. Effect order
    // guarantees this runs after every old-key cleanup and before the new
    // key's save/restore effects.
    if (activeAnchorKeyRef.current !== anchorKey) {
      activeAnchorKeyRef.current = anchorKey;
      restoreAttemptedRef.current = '';
      restoreSettledSignatureRef.current = '';
      userCancelledRestoreRef.current = '';
      restoreAppliedAtRef.current = 0;
      userScrollInputAtRef.current = 0;
      nonUserScrollTaintedRef.current = false;
      lastAppliedScrollTopRef.current = -1;
    }

    function markUserScrollInput() {
      userCancelledRestoreRef.current = anchorKey;
      userScrollInputAtRef.current = nowMs();
      // User input ratifies the current position: any scroll that follows is
      // theirs to save, so a standing non-user taint no longer applies.
      nonUserScrollTaintedRef.current = false;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (!restoreCancelKeys.has(event.key)) return;
      markUserScrollInput();
    }

    function handleTopReset() {
      savedScrollAnchors[anchorKey] = {
        anchorKey,
        offset: 0,
        scrollTop: 0
      };
      restoreSettledSignatureRef.current =
        getSavedAnchorRestoreSignature(anchorKey);
      userCancelledRestoreRef.current = '';
      // The app just re-established the position (top), so any standing
      // non-user taint is superseded and the browser's async echo of the
      // reset scroll must compare against 0, not a stale applied position.
      nonUserScrollTaintedRef.current = false;
      lastAppliedScrollTopRef.current = 0;
      recordScrollDiagnostic({ type: 'top-reset', anchorKey, scrollTop: 0 });
    }

    // An external write to this instance's anchor has no restore in flight;
    // without marking it settled here, saveShouldWaitForPendingRestore would
    // block every subsequent natural scroll save until remount (e.g. after a
    // popup link click that never navigates). Per-instance on purpose: a
    // remounted hook must still treat that saved anchor as a pending restore.
    function handleExternalSave(savedAnchorKey: string) {
      if (savedAnchorKey !== anchorKey) return;
      restoreSettledSignatureRef.current =
        getSavedAnchorRestoreSignature(anchorKey);
    }

    window.addEventListener('wheel', markUserScrollInput, {
      capture: true,
      passive: true
    });
    window.addEventListener('touchmove', markUserScrollInput, {
      capture: true,
      passive: true
    });
    window.addEventListener('keydown', handleKeyDown, {
      capture: true
    });
    const removeRestoreCancelListener =
      addScrollAnchorRestoreCancelListener(markUserScrollInput);
    const removeTopResetListener =
      addScrollAnchorTopResetListener(handleTopReset);
    const removeExternalSaveListener =
      addScrollAnchorExternalSaveListener(handleExternalSave);

    return () => {
      window.removeEventListener('wheel', markUserScrollInput, {
        capture: true
      });
      window.removeEventListener('touchmove', markUserScrollInput, {
        capture: true
      });
      window.removeEventListener('keydown', handleKeyDown, {
        capture: true
      });
      removeRestoreCancelListener();
      removeTopResetListener();
      removeExternalSaveListener();
    };
  }, [anchorKey]);

  // A forward navigation must not show the previous page's scroll offset.
  // Route changes run in a transition, so the document keeps the old offset
  // (e.g. deep in the home feed) when the new page mounts, and the 'top'
  // initial scroll below only runs once the page's data is ready (and not at
  // all if restores are suppressed at that moment). Meanwhile the page sat
  // mid-way or, when shorter than the old offset, clamped to its bottom
  // (Mikey, 2026-09-29: ~30% of home feed taps opened a post scrolled to the
  // bottom). Runs after the old page's cleanups (its teardown save already
  // happened), only when nothing is going to be restored.
  //
  // Writing 0 is not enough on iOS (document scroller): when the new page first
  // renders shorter than the old offset, scrollTop already reads a clamped 0
  // here, yet WebKit re-applies the old offset once the content grows the
  // document (owner trace 2026-10-06: /comments/335037 opened at 869). So a
  // forward navigation that will show the top also opens a top pin window
  // (startTopPinWindow) whether or not anything had to be written. Writing 0
  // when it already reads 0 is skipped on purpose: the DOM value is unchanged
  // so WebKit treats it as a no-op, while it would still suppress saves and
  // log an initial-scroll on every forward navigation.
  //
  // Only a real navigation (a new location key) opens the pin; an anchor key
  // change on the same location (search text keyed anchors) only gets the
  // one-off top write. The gate is consumed on every key change, POP and
  // skips included, in its own effect that runs just before this one.
  const locationKey = useLocation().key;
  useLayoutEffect(() => {
    arrivedByNavigationRef.current =
      initialScrollType === 'top' && topPinNavigationGate.consume(locationKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey]);
  const navigationType = useAppNavigationType();
  useLayoutEffect(() => {
    if (navigationType === 'POP') return;
    if (initialScrollType !== 'top') return;
    if (!appOwnsScrollRestoration()) return;
    if (!ignoreSavedAnchor && savedScrollAnchors[anchorKey]) return;
    // The restore effect's no-saved-anchor top scroll for this key may start
    // (or extend) the pin too; a saved-anchor restore never does. No reset on
    // the early returns above: this effect runs on every key change, so a
    // value left from an earlier key can never equal the current one.
    const opensTopPin = arrivedByNavigationRef.current;
    if (opensTopPin) forwardNavTopPinKeyRef.current = anchorKey;
    const scroller = getActiveScroller();
    const scrollTop = Math.round(getScrollTop(scroller));
    if (scrollTop <= 0) {
      if (!opensTopPin) return;
      startTopPinWindow({
        anchorKey,
        note: 'forward-nav-mount',
        refs: topPinRefsRef.current
      });
      return;
    }
    recordScrollDiagnostic({
      type: 'initial-scroll',
      anchorKey,
      scrollTop,
      itemsReady,
      reason: initialScrollType,
      note: 'forward-nav-mount'
    });
    applyInitialScroll({ scroller, targetRef: null, type: 'top' });
    restoreAppliedAtRef.current = nowMs();
    nonUserScrollTaintedRef.current = false;
    lastAppliedScrollTopRef.current = getScrollTop(scroller);
    if (!opensTopPin) return;
    startTopPinWindow({
      anchorKey,
      note: 'forward-nav-mount',
      refs: topPinRefsRef.current
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey]);

  // The pin belongs to one key: a key change or unmount ends it. Reads the
  // live ref on purpose — the pin open at teardown, not the one at setup.
  useLayoutEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      topPinRef.current?.stop('teardown');
    };
  }, [anchorKey]);

  useLayoutEffect(() => {
    if (!itemsReady || !containerRef.current) return;
    const scroller = getAppScroller();
    const container = containerRef.current;
    let frame = 0;

    function handleScroll() {
      if (scrollAnchorSavesAreSuppressed()) {
        taintIfScrollLeftAppliedPosition('suppressed-window');
        return;
      }
      if (saveShouldWaitForPendingRestore()) return;
      if (scrollIsNonUserWhileGuarded()) return;
      saveAnchorAndMarkSettled();
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        if (scrollAnchorSavesAreSuppressed()) {
          taintIfScrollLeftAppliedPosition('suppressed-window');
          return;
        }
        if (saveShouldWaitForPendingRestore()) return;
        if (scrollIsNonUserWhileGuarded()) return;
        saveAnchorAndMarkSettled();
      });
    }

    // Single detection predicate for non-user scrolls, shared by the
    // suppressed branch, the guarded branch, and the teardown check. The
    // hook's own scroll events either carry the in-flight flag or sit at the
    // applied position (including delayed native echoes), so under guard, a
    // scroll that has LEFT that position is browser-initiated and must
    // taint. The teardown call also covers an override written in the final
    // frame whose scroll event never got delivered before unmount.
    function taintIfScrollLeftAppliedPosition(note: string) {
      if (applyingProgrammaticScroll) return;
      if (nonUserScrollTaintedRef.current) return;
      if (!nonUserScrollGuardHolds()) return;
      const appliedScrollTop = lastAppliedScrollTopRef.current;
      if (appliedScrollTop < 0) return;
      const currentScrollTop = getScrollTop(getActiveScroller());
      if (
        Math.abs(currentScrollTop - appliedScrollTop) <=
        appliedScrollTopTolerancePx
      ) {
        return;
      }
      nonUserScrollTaintedRef.current = true;
      recordScrollDiagnostic({
        type: 'save-skipped-non-user-scroll',
        anchorKey,
        scrollTop: Math.round(currentScrollTop),
        note
      });
    }

    // Only guards document-scroller mode (iOS: #App.ios is overflow-y
    // visible): that is where the browser's own history restore can move the
    // scroller without user input, and touch/wheel/key input coverage is
    // exhaustive there. Element-scroller platforms have native scrollbars,
    // whose drags emit scroll events with no wheel/touch/key signal (Firefox
    // dispatches no pointer events for them at all) — and no browser-initiated
    // history restore targets the element scroller, so the guard must stay
    // inert there or real user scrolls would be dropped.
    function nonUserScrollGuardHolds() {
      // Before the first in-app pushState, scrollRestoration is still 'auto'
      // and a browser-initiated scroll is the LEGITIMATE deferred restore of
      // a reload / bfcache-evicted return — adopt it as the anchor instead
      // of tainting it. No same-document traversal (and thus no race) can
      // exist yet.
      if (!appOwnsScrollRestoration()) return false;
      const appliedAt = restoreAppliedAtRef.current;
      if (!appliedAt) return false;
      if (nowMs() - appliedAt > postRestoreNonUserSaveGuardMs) return false;
      if (userScrollInputAtRef.current >= appliedAt) return false;
      if (getActiveScroller()) return false;
      return true;
    }

    // A scroll event arriving under guard that has LEFT the applied position
    // is browser-initiated: it taints the position as no longer matching what
    // the hook applied. The position check matters here too, not just in the
    // suppressed branch — the browser can deliver the native echo of the
    // hook's own scrollTop write after the 250ms suppression lapses (busy
    // main thread during a route remount), and that echo sits at the applied
    // position, so it must save normally rather than taint. Taint is a
    // STANDING block — a browser-chosen offset must never become the anchor
    // without ratification, and dwell time is not ratification. It clears
    // only when user scroll input arrives, when the hook applies a scroll
    // (e.g. an observer re-pin correcting the override), or on a top reset.
    function scrollIsNonUserWhileGuarded() {
      if (nonUserScrollTaintedRef.current) {
        recordScrollDiagnostic({
          type: 'save-skipped-non-user-scroll',
          anchorKey,
          scrollTop: Math.round(getScrollTop(getActiveScroller())),
          note: 'standing-taint'
        });
        return true;
      }
      taintIfScrollLeftAppliedPosition('guarded');
      return nonUserScrollTaintedRef.current;
    }

    function saveAnchorAndMarkSettled() {
      saveCurrentAnchor(anchorKey, container, getActiveScroller());
      restoreSettledSignatureRef.current =
        getSavedAnchorRestoreSignature(anchorKey);
    }

    function saveShouldWaitForPendingRestore() {
      const restoreSignature = getSavedAnchorRestoreSignature(anchorKey);
      if (
        activeIgnoredSavedAnchorKeyRef.current === anchorKey ||
        (!!restoreSignature &&
          ignoredSavedAnchorSignaturesRef.current[anchorKey] ===
            restoreSignature)
      ) {
        return false;
      }
      const waiting =
        !!restoreSignature &&
        restoreSettledSignatureRef.current !== restoreSignature;
      // This branch used to skip silently, which hid a save freeze in field
      // captures; log once per pending signature so a wedged state is visible
      // without flooding the ring buffer.
      if (
        waiting &&
        loggedPendingSkipSignatureRef.current !== restoreSignature
      ) {
        loggedPendingSkipSignatureRef.current = restoreSignature;
        recordScrollDiagnostic({
          type: 'save-skipped-pending-restore',
          anchorKey,
          scrollTop: Math.round(getScrollTop(getActiveScroller()))
        });
      }
      return waiting;
    }

    window.addEventListener('scroll', handleScroll, { passive: true });
    scroller?.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      taintIfScrollLeftAppliedPosition('teardown-check');
      if (
        !scrollAnchorSavesAreSuppressed() &&
        !saveShouldWaitForPendingRestore() &&
        !nonUserScrollTaintedRef.current
      ) {
        saveAnchorAndMarkSettled();
      }
      window.removeEventListener('scroll', handleScroll);
      scroller?.removeEventListener('scroll', handleScroll);
    };
  }, [anchorKey, containerRef, itemsReady]);

  useLayoutEffect(() => {
    if (!itemsReady || !containerRef.current) return;
    const savedAnchorRestoreSignature =
      getSavedAnchorRestoreSignature(anchorKey);
    if (
      activeIgnoredSavedAnchorKeyRef.current === anchorKey &&
      savedAnchorRestoreSignature
    ) {
      ignoredSavedAnchorSignaturesRef.current[anchorKey] =
        savedAnchorRestoreSignature;
    }
    const savedAnchorIsIgnored =
      activeIgnoredSavedAnchorKeyRef.current === anchorKey ||
      (!!savedAnchorRestoreSignature &&
        ignoredSavedAnchorSignaturesRef.current[anchorKey] ===
          savedAnchorRestoreSignature);
    const savedAnchor =
      savedAnchorIsIgnored ? undefined : savedScrollAnchors[anchorKey];
    const restoreSignature = savedAnchor ? savedAnchorRestoreSignature : '';
    if (scrollAnchorRestoresAreSuppressed()) {
      markSavedAnchorRestoreSettled();
      return;
    }
    if (userCancelledRestoreRef.current === anchorKey) {
      markSavedAnchorRestoreSettled();
      return;
    }
    if (!savedAnchor) {
      const initialScrollKey = `${anchorKey}:initial`;
      if (initialScrollAttemptedRef.current === initialScrollKey) return;
      initialScrollAttemptedRef.current = initialScrollKey;
      // While the browser still owns scroll (pre-first-push), a programmatic
      // initial scroll would CANCEL the browser's deferred native restore
      // (browsers abandon pending restoration once script scrolls the
      // document), losing the reload / bfcache-evicted-return position.
      // Fresh navigations start at top anyway, so skipping loses nothing.
      if (!appOwnsScrollRestoration()) {
        recordScrollDiagnostic({
          type: 'initial-scroll',
          anchorKey,
          scrollTop: Math.round(getScrollTop(getActiveScroller())),
          itemsReady,
          reason: initialScrollType,
          note: 'skipped-browser-owned'
        });
        return;
      }
      recordScrollDiagnostic({
        type: 'initial-scroll',
        anchorKey,
        scrollTop: Math.round(getScrollTop(getActiveScroller())),
        itemsReady,
        reason: initialScrollType,
        note: savedAnchorIsIgnored ? 'saved-anchor-ignored' : 'no-saved-anchor'
      });
      applyInitialScroll({
        scroller: getActiveScroller(),
        targetRef: initialScrollTargetRef,
        topOffset: initialScrollTopOffset,
        type: initialScrollType
      });
      if (initialScrollType !== 'preserve') {
        markProgrammaticScrollApplied();
      }
      if (
        initialScrollType === 'top' &&
        forwardNavTopPinKeyRef.current === anchorKey
      ) {
        startTopPinWindow({
          anchorKey,
          note: savedAnchorIsIgnored
            ? 'saved-anchor-ignored'
            : 'no-saved-anchor',
          refs: topPinRefsRef.current
        });
      }
      return;
    }

    // Restoring a saved position: a top pin must not drag it back to 0.
    topPinRef.current?.stop('saved-anchor-restore');

    const anchorToRestore = savedAnchor;
    const restoreKey = `${anchorKey}:${anchorToRestore.primaryId || ''}:${
      anchorToRestore.secondaryId || ''
    }:${anchorToRestore.contentKey || ''}`;
    if (restoreAttemptedRef.current === restoreKey) {
      markSavedAnchorRestoreSettled();
      return;
    }
    restoreAttemptedRef.current = restoreKey;

    let attempts = 0;
    let restoreFrame = 0;
    let settleFrame = 0;
    let settleAttempts = 0;
    let observerTimer = 0;
    let resizeObserver: ResizeObserver | null = null;
    let mutationObserver: MutationObserver | null = null;
    let restoreCancelled = false;
    let cancelListenersAttached = false;
    let removeRestoreCancelSignalListener: (() => void) | null = null;
    let landedOnAnchor = false;
    const restoreStartedAt = nowMs();

    function diag(type: string, extra: Record<string, unknown> = {}) {
      recordScrollDiagnostic({
        type,
        anchorKey,
        scrollTop: Math.round(getScrollTop(getActiveScroller())),
        itemsReady,
        primaryId: anchorToRestore.primaryId,
        secondaryId: anchorToRestore.secondaryId,
        savedScrollTop: anchorToRestore.scrollTop,
        savedOffset: anchorToRestore.offset,
        ...extra
      });
    }

    diag('restore-start');

    function restore() {
      if (restoreCancelled) return;
      if (scrollAnchorRestoresAreSuppressed()) {
        diag('restore-suppressed');
        cancelPendingRestore('suppressed');
        return;
      }
      const container = containerRef.current;
      if (!container) return;
      const scroller = getActiveScroller();
      const anchorElement = findAnchorElement(container, anchorToRestore);
      if (!anchorElement) {
        restoreToSavedScrollTop(anchorToRestore, scroller);
        markProgrammaticScrollApplied();
        diag('anchor-missing-fallback-scrolltop', { attempt: attempts });
        markRestoreSettledIfNoAnchorIdentity();
        attempts += 1;
        if (attempts < 12) {
          restoreFrame = window.requestAnimationFrame(restore);
        } else if (!resizeObserver && !mutationObserver) {
          markRestoreSettled();
        }
        return;
      }
      const computedScrollTop = restoreToAnchor(
        anchorElement,
        anchorToRestore.offset,
        scroller
      );
      markProgrammaticScrollApplied();
      landedOnAnchor = true;
      diag('restore-to-anchor', { computedScrollTop });
      markRestoreSettled();
      settleAnchor();
    }

    addRestoreCancelListeners();
    restore();

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(() => {
        restore();
      });
      resizeObserver.observe(containerRef.current);
    }
    if (typeof MutationObserver === 'function') {
      mutationObserver = new MutationObserver(() => {
        restore();
      });
      mutationObserver.observe(containerRef.current, {
        childList: true,
        subtree: true
      });
    }
    if (resizeObserver || mutationObserver) {
      observerTimer = window.setTimeout(() => {
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
        markRestoreSettled();
        removeRestoreCancelListeners();
      }, 3000);
    }

    return () => {
      cancelPendingRestore('cleanup');
    };

    function addRestoreCancelListeners() {
      if (cancelListenersAttached) return;
      cancelListenersAttached = true;
      window.addEventListener('wheel', handleWheelCancel, {
        capture: true,
        passive: true
      });
      window.addEventListener('touchmove', handleTouchCancel, {
        capture: true,
        passive: true
      });
      window.addEventListener('keydown', handleRestoreKeyDown, {
        capture: true
      });
      removeRestoreCancelSignalListener =
        addScrollAnchorRestoreCancelListener(() =>
          cancelPendingRestore('signal')
        );
    }

    function removeRestoreCancelListeners() {
      if (!cancelListenersAttached) return;
      cancelListenersAttached = false;
      window.removeEventListener('wheel', handleWheelCancel, {
        capture: true
      });
      window.removeEventListener('touchmove', handleTouchCancel, {
        capture: true
      });
      window.removeEventListener('keydown', handleRestoreKeyDown, {
        capture: true
      });
      removeRestoreCancelSignalListener?.();
      removeRestoreCancelSignalListener = null;
    }

    function handleWheelCancel() {
      handleUserScrollInput('wheel');
    }

    function handleTouchCancel() {
      handleUserScrollInput('touchmove');
    }

    // While a restore is in flight and has NOT yet pinned the saved anchor,
    // ignore incidental pointer input for a short grace window so iOS momentum
    // / the navigation tap can't strand the scroll at the fallback position.
    // Once the anchor is pinned (or the grace elapses) we honor input normally
    // so a deliberate scroll is never fought.
    function handleUserScrollInput(reason: string) {
      if (
        !landedOnAnchor &&
        nowMs() - restoreStartedAt < restoreCancelGraceMs
      ) {
        diag('cancel-suppressed', { reason });
        return;
      }
      diag('cancel', { reason });
      cancelPendingRestore(reason);
    }

    function handleRestoreKeyDown(event: KeyboardEvent) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (!restoreCancelKeys.has(event.key)) return;
      diag('cancel', { reason: 'keydown' });
      cancelPendingRestore('keydown');
    }

    function cancelPendingRestore(reason = 'cancel') {
      if (restoreCancelled) return;
      restoreCancelled = true;
      // Effect teardown on ordinary unmount/navigation is normal bookkeeping,
      // not an interrupted restore — log it as a distinct teardown so it never
      // counts toward the "restores cancelled" signal. Genuine cancels (touch /
      // wheel / key / coordinator signal) stay as restore-cancelled, and the
      // note records whether the restore had pinned the anchor before it ended.
      diag(reason === 'cleanup' ? 'restore-teardown' : 'restore-cancelled', {
        reason,
        note: landedOnAnchor ? 'landed' : 'not-landed'
      });
      markRestoreSettled();
      if (restoreFrame) window.cancelAnimationFrame(restoreFrame);
      if (settleFrame) window.cancelAnimationFrame(settleFrame);
      if (observerTimer) window.clearTimeout(observerTimer);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      removeRestoreCancelListeners();
    }

    function settleAnchor() {
      if (restoreCancelled) return;
      if (settleFrame) return;

      settleFrame = window.requestAnimationFrame(function settle() {
        if (restoreCancelled) return;
        if (scrollAnchorRestoresAreSuppressed()) {
          cancelPendingRestore('suppressed');
          return;
        }
        settleFrame = 0;
        const container = containerRef.current;
        if (!container) return;
        const scroller = getActiveScroller();
        const anchorElement = findAnchorElement(container, anchorToRestore);
        if (!anchorElement) {
          restoreToSavedScrollTop(anchorToRestore, scroller);
          markProgrammaticScrollApplied();
          markRestoreSettledIfNoAnchorIdentity();
          return;
        }

        const computedScrollTop = restoreToAnchor(
          anchorElement,
          anchorToRestore.offset,
          scroller
        );
        markProgrammaticScrollApplied();
        landedOnAnchor = true;
        markRestoreSettled();
        settleAttempts += 1;
        if (settleAttempts >= 11) {
          diag('settle-final', { computedScrollTop, attempt: settleAttempts });
        }
        if (settleAttempts < 12 && !restoreCancelled) {
          settleFrame = window.requestAnimationFrame(settle);
        }
      });
    }

    // A hook-applied scroll re-establishes "position matches what we applied":
    // it restarts the non-user-save guard window and clears any taint from an
    // earlier browser-initiated scroll (e.g. an observer re-pin correcting it).
    // The applied position is read back after the write so clamping is
    // captured; it is the baseline the suppression-window taint check
    // compares against.
    function markProgrammaticScrollApplied() {
      restoreAppliedAtRef.current = nowMs();
      nonUserScrollTaintedRef.current = false;
      lastAppliedScrollTopRef.current = getScrollTop(getActiveScroller());
    }

    // Re-read the signature at call time instead of using the effect-start
    // capture: a scroll event that lands between settle re-pins saves a
    // fresh anchor (layout can drift subpixel while embeds/images settle),
    // and writing the stale captured signature here afterwards would leave
    // the settled ref permanently behind the saved anchor — every later
    // save, including the teardown save, then skips via
    // saveShouldWaitForPendingRestore and the anchor freezes at the restored
    // position until reload.
    function markRestoreSettled() {
      restoreSettledSignatureRef.current =
        getSavedAnchorRestoreSignature(anchorKey);
    }

    function markRestoreSettledIfNoAnchorIdentity() {
      if (!savedAnchorHasElementIdentity(anchorToRestore)) {
        markRestoreSettled();
      }
    }

    function markSavedAnchorRestoreSettled() {
      if (restoreSignature) {
        restoreSettledSignatureRef.current = restoreSignature;
      }
    }
  }, [
    anchorKey,
    containerRef,
    initialScrollTargetRef,
    initialScrollTopOffset,
    initialScrollType,
    itemsReady,
    ignoreSavedAnchor
  ]);
}

function savedAnchorHasElementIdentity(savedAnchor: SavedScrollAnchor) {
  return Boolean(
    savedAnchor.primaryId || savedAnchor.secondaryId || savedAnchor.contentKey
  );
}

function getSavedAnchorRestoreSignature(anchorKey: string) {
  const savedAnchor = savedScrollAnchors[anchorKey];
  if (!savedAnchor) return '';
  return `${anchorKey}:${savedAnchor.primaryId || ''}:${
    savedAnchor.secondaryId || ''
  }:${savedAnchor.contentKey || ''}:${savedAnchor.offset}:${savedAnchor.scrollTop}`;
}

/** A copy of the saved anchor for this key (homeFeedLifeline keeps it). */
export function getSavedScrollAnchor(anchorKey: string) {
  const saved = savedScrollAnchors[anchorKey];
  return saved ? { ...saved } : null;
}

/** Put back an anchor saved before the tab was reloaded (homeFeedLifeline). */
export function seedSavedScrollAnchor(anchor: SavedScrollAnchor) {
  if (!anchor?.anchorKey || savedScrollAnchors[anchor.anchorKey]) return;
  savedScrollAnchors[anchor.anchorKey] = { ...anchor };
}

export function clearSavedScrollAnchors(...anchorKeys: string[]) {
  for (const anchorKey of anchorKeys) {
    delete savedScrollAnchors[anchorKey];
  }
}

export function saveScrollAnchorForElement(
  sourceElement: HTMLElement | null,
  anchorKey?: string
) {
  if (!sourceElement) return;
  const anchorElement = sourceElement.closest<HTMLElement>(
    '[data-scroll-anchor-id], [data-scroll-anchor-content-key]'
  );
  if (anchorKey && anchorElement) {
    saveAnchorElement(anchorKey, anchorElement, getActiveScroller());
    suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
    return;
  }

  if (!anchorKey && anchorElement) {
    const groupKey = anchorElement.closest<HTMLElement>(
      '[data-scroll-anchor-group-key]'
    )?.dataset.scrollAnchorGroupKey;
    if (groupKey) {
      saveAnchorElement(groupKey, anchorElement, getActiveScroller());
      suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
      return;
    }
  }

  const contentPagePanel = sourceElement.closest<HTMLElement>(
    '[data-content-page="true"]'
  );
  const contentAnchorContainer = contentPagePanel?.closest<HTMLElement>(
    '[data-scroll-anchor-id^="content:"]'
  );
  const contentAnchorKey = contentAnchorContainer?.dataset.scrollAnchorId;
  if (!contentAnchorKey || !contentAnchorContainer) return;

  if (
    anchorElement &&
    anchorElement !== contentAnchorContainer &&
    contentAnchorContainer.contains(anchorElement)
  ) {
    saveAnchorElement(contentAnchorKey, anchorElement, getActiveScroller());
    suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
    return;
  }

  saveCurrentAnchor(
    contentAnchorKey,
    contentAnchorContainer,
    getActiveScroller()
  );
  notifyScrollAnchorExternalSave(contentAnchorKey);
  suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
}

interface TopPinRefs {
  topPin: { current: TopPinWindow | null };
  userScrollInputAt: { current: number };
  restoreAppliedAt: { current: number };
  nonUserScrollTainted: { current: boolean };
  lastAppliedScrollTop: { current: number };
}

// Scroll input only. A bare tap (touchstart / pointerdown) does not end the
// pin: a tap inside the window must not let WebKit's stale offset win later,
// a tap that does move the page is re-pinned like any non-user offset, and a
// swipe or momentum scroll always starts with touchmove. Scroll keys are
// filtered in handleUserInput.
const topPinUserInputEvents = [
  'wheel',
  'touchmove',
  'keydown',
  'focusin'
] as const;

// Never opens while a text field has focus (elementIsEditableField).
//
// Opens (or, if one is already open, extends) the post-forward-navigation top
// pin — see scrollTopPin.ts for why. Only in document-scroller mode (iOS:
// #App.ios is overflow-y visible), the same scope as the non-user save guard:
// that is where WebKit re-applies the old document offset, and where
// touch/wheel/key input coverage is exhaustive; element-scroller scrollbar
// drags emit no input event, so pinning there could fight a real scroll.
//
// While open, a scroll event or a document resize (WebKit can move the offset
// as the content grows without a timely scroll event) that finds the page
// off the top puts it back with the hook's normal programmatic-scroll
// bookkeeping, so the re-pin is neither saved as a user position nor left
// tainted, and logs a 'top-repin' with the stray offset for the owner trace.
// It ends for good on touchmove / wheel / scroll-key / focus input, on a
// deliberate app scroll (releaseScrollAnchorTopPin), after topPinWindowMs
// without a new top apply, at the hard cap, on a saved-anchor restore, or on
// key change / unmount.
function startTopPinWindow({
  anchorKey,
  note,
  refs
}: {
  anchorKey: string;
  note: string;
  refs: TopPinRefs;
}) {
  if (typeof window === 'undefined') return;
  if (getActiveScroller()) return;
  if (elementIsEditableField(document.activeElement as HTMLElement | null)) {
    return;
  }
  const existing = refs.topPin.current;
  if (existing?.isActive()) {
    existing.extend();
    return;
  }

  let expiryTimer = 0;
  let resizeObserver: ResizeObserver | null = null;
  let removeReleaseListener: (() => void) | null = null;
  const pin: TopPinWindow = createTopPinWindow({
    now: nowMs,
    readScrollTop: () => getScrollTop(getActiveScroller()),
    userInputSince: (time) => refs.userScrollInputAt.current >= time,
    repin(strayScrollTop, trigger, count) {
      applyInitialScroll({
        scroller: getActiveScroller(),
        targetRef: null,
        type: 'top'
      });
      refs.restoreAppliedAt.current = nowMs();
      refs.nonUserScrollTainted.current = false;
      refs.lastAppliedScrollTop.current = getScrollTop(getActiveScroller());
      recordScrollDiagnostic({
        type: 'top-repin',
        anchorKey,
        scrollTop: Math.round(strayScrollTop),
        reason: trigger,
        attempt: count,
        note
      });
    },
    onEnd(reason, repins) {
      if (expiryTimer) window.clearTimeout(expiryTimer);
      resizeObserver?.disconnect();
      removeReleaseListener?.();
      window.removeEventListener('scroll', handleScroll);
      for (const eventName of topPinUserInputEvents) {
        window.removeEventListener(eventName, handleUserInput, {
          capture: true
        });
      }
      if (refs.topPin.current === pin) refs.topPin.current = null;
      // Quiet windows (nothing to fight) stay out of the trace.
      if (repins > 0) {
        recordScrollDiagnostic({
          type: 'top-pin-end',
          anchorKey,
          scrollTop: Math.round(getScrollTop(getActiveScroller())),
          attempt: repins,
          reason
        });
      }
    }
  });
  refs.topPin.current = pin;

  function handleScroll() {
    pin.check('scroll');
  }

  function handleUserInput(event: Event) {
    if (event.type === 'keydown') {
      const keyEvent = event as KeyboardEvent;
      if (keyEvent.altKey || keyEvent.ctrlKey || keyEvent.metaKey) return;
      if (!restoreCancelKeys.has(keyEvent.key)) return;
    }
    pin.stop('user-input');
  }

  // Re-checks at the window's end: check() closes an expired window, and an
  // extended one gets another round (bounded by the hard cap).
  function scheduleExpiryCheck() {
    expiryTimer = window.setTimeout(() => {
      expiryTimer = 0;
      pin.check('expiry-check');
      if (pin.isActive()) scheduleExpiryCheck();
    }, topPinWindowMs + 50);
  }

  window.addEventListener('scroll', handleScroll, { passive: true });
  for (const eventName of topPinUserInputEvents) {
    window.addEventListener(eventName, handleUserInput, {
      capture: true,
      passive: true
    });
  }
  removeReleaseListener = addScrollAnchorTopPinReleaseListener(() =>
    pin.stop('released')
  );
  if (typeof ResizeObserver === 'function') {
    resizeObserver = new ResizeObserver(() => {
      pin.check('resize');
    });
    for (const element of [
      document.documentElement,
      document.body,
      getAppScroller()
    ]) {
      if (element) resizeObserver.observe(element);
    }
  }
  scheduleExpiryCheck();
}

function applyInitialScroll({
  scroller,
  targetRef,
  topOffset,
  type
}: {
  scroller: HTMLElement | null;
  targetRef: RefObject<HTMLElement | null> | null;
  topOffset?: number;
  type: InitialScrollPolicy['type'];
}) {
  if (type === 'preserve') return;
  if (type === 'top') {
    suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
    setScrollTop(scroller, 0);
    return;
  }

  const targetElement = targetRef?.current;
  if (!targetElement) return;
  suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
  scrollElementToTop(targetElement, topOffset || 0, scroller);
}

function saveAnchorElement(
  anchorKey: string,
  anchorElement: HTMLElement,
  scroller: HTMLElement | null
) {
  const viewportTop = getViewportTop(scroller);
  const rect = anchorElement.getBoundingClientRect();
  savedScrollAnchors[anchorKey] = {
    anchorKey,
    primaryId: anchorElement.dataset.scrollAnchorId,
    secondaryId: anchorElement.dataset.scrollAnchorSecondaryId,
    contentKey: anchorElement.dataset.scrollAnchorContentKey,
    offset: viewportTop - rect.top,
    scrollTop: getScrollTop(scroller)
  };
  notifyScrollAnchorExternalSave(anchorKey);
  recordAnchorSave(anchorKey, 'element');
}

function recordAnchorSave(anchorKey: string, note: string) {
  // Routine saves run on every scroll event and frame; only the explicit
  // element save (a feed card tap) goes to the owner trace, the rest only to
  // the opt-in local capture. Restore events still carry the saved values.
  if (note !== 'element' && !isScrollDiagnosticsLoggingEnabled()) return;
  if (!shouldRecordScrollDiagnostics()) return;
  const saved = savedScrollAnchors[anchorKey];
  if (!saved) return;
  recordScrollDiagnostic({
    type: 'save',
    anchorKey,
    primaryId: saved.primaryId,
    secondaryId: saved.secondaryId,
    savedScrollTop: saved.scrollTop,
    savedOffset: Math.round(saved.offset),
    scrollTop: Math.round(saved.scrollTop),
    note
  });
}

function saveCurrentAnchor(
  anchorKey: string,
  container: HTMLElement | null,
  scroller: HTMLElement | null
) {
  if (!container) return;
  const scrollTop = getScrollTop(scroller);
  if (!container.isConnected) {
    if (savedScrollAnchors[anchorKey]) return;
    savedScrollAnchors[anchorKey] = {
      anchorKey,
      offset: 0,
      scrollTop
    };
    recordAnchorSave(anchorKey, 'disconnected');
    return;
  }

  const items = getScrollAnchorItems(container);
  if (items.length === 0) {
    savedScrollAnchors[anchorKey] = {
      anchorKey,
      offset: 0,
      scrollTop
    };
    recordAnchorSave(anchorKey, 'no-items');
    return;
  }

  const viewportTop = getViewportTop(scroller);
  const viewportBottom = getViewportBottom(scroller);
  const anchorElement = findCurrentAnchorElement({
    items,
    viewportBottom,
    viewportTop
  });
  if (!anchorElement) {
    savedScrollAnchors[anchorKey] = {
      anchorKey,
      offset: 0,
      scrollTop
    };
    recordAnchorSave(anchorKey, 'no-anchor-in-view');
    return;
  }

  const rect = anchorElement.getBoundingClientRect();
  savedScrollAnchors[anchorKey] = {
    anchorKey,
    primaryId: anchorElement.dataset.scrollAnchorId,
    secondaryId: anchorElement.dataset.scrollAnchorSecondaryId,
    contentKey: anchorElement.dataset.scrollAnchorContentKey,
    offset: viewportTop - rect.top,
    scrollTop
  };
  recordAnchorSave(anchorKey, 'current');
}

function findCurrentAnchorElement({
  items,
  viewportBottom,
  viewportTop
}: {
  items: HTMLElement[];
  viewportBottom: number;
  viewportTop: number;
}) {
  const viewportHeight = Math.max(0, viewportBottom - viewportTop);
  const anchorLine =
    viewportTop + Math.min(Math.max(viewportHeight * 0.1, 72), 140);
  let containingAnchor: { item: HTMLElement; height: number } | null = null;
  let firstLowerAnchor: HTMLElement | null = null;
  let nearestAnchor: { item: HTMLElement; distance: number } | null = null;

  for (const item of items) {
    const rect = item.getBoundingClientRect();
    const distanceFromAnchorLine =
      rect.top > anchorLine
        ? rect.top - anchorLine
        : Math.max(0, anchorLine - rect.bottom);
    if (!nearestAnchor || distanceFromAnchorLine < nearestAnchor.distance) {
      nearestAnchor = {
        item,
        distance: distanceFromAnchorLine
      };
    }

    const visibleHeight =
      Math.min(rect.bottom, viewportBottom) - Math.max(rect.top, viewportTop);
    if (visibleHeight <= 1) continue;
    if (rect.top <= anchorLine && rect.bottom >= anchorLine) {
      const height = Math.max(rect.height, 1);
      if (!containingAnchor || height < containingAnchor.height) {
        containingAnchor = { item, height };
      }
      continue;
    }
    if (rect.bottom >= anchorLine && !firstLowerAnchor) {
      firstLowerAnchor = item;
    }
  }

  return containingAnchor?.item || firstLowerAnchor || nearestAnchor?.item;
}

function findAnchorElement(
  container: HTMLElement,
  savedAnchor: SavedScrollAnchor
) {
  const items = getScrollAnchorItems(container);
  if (savedAnchor.primaryId) {
    const primaryMatch = items.find(
      (item) => item.dataset.scrollAnchorId === savedAnchor.primaryId
    );
    if (primaryMatch) return primaryMatch;
  }

  if (savedAnchor.secondaryId) {
    const secondaryMatch = items.find(
      (item) =>
        item.dataset.scrollAnchorSecondaryId === savedAnchor.secondaryId
    );
    if (secondaryMatch) return secondaryMatch;
  }

  if (savedAnchor.contentKey) {
    return items.find(
      (item) => item.dataset.scrollAnchorContentKey === savedAnchor.contentKey
    );
  }

  return undefined;
}

function getScrollAnchorItems(container: HTMLElement) {
  const items = Array.from(
    container.querySelectorAll<HTMLElement>(
      '[data-scroll-anchor-id], [data-scroll-anchor-content-key]'
    )
  );
  if (
    container.dataset.scrollAnchorId !== undefined ||
    container.dataset.scrollAnchorContentKey !== undefined
  ) {
    items.unshift(container);
  }
  return items.filter(
    (item) =>
      item.dataset.scrollAnchorId !== undefined ||
      item.dataset.scrollAnchorContentKey !== undefined
  );
}

function restoreToAnchor(
  anchorElement: HTMLElement,
  offset: number,
  scroller: HTMLElement | null
) {
  const viewportTop = getViewportTop(scroller);
  const rect = anchorElement.getBoundingClientRect();
  const currentScrollTop = getScrollTop(scroller);
  const nextScrollTop = Math.max(
    0,
    currentScrollTop + rect.top - viewportTop + offset
  );
  suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
  setScrollTop(scroller, nextScrollTop);
  return Math.round(nextScrollTop);
}

function nowMs() {
  try {
    return performance.now();
  } catch {
    return Date.now();
  }
}

function restoreToSavedScrollTop(
  savedAnchor: SavedScrollAnchor,
  scroller: HTMLElement | null
) {
  if (!Number.isFinite(savedAnchor.scrollTop)) return;
  suppressScrollAnchorSaves(restoreSaveSuppressionDurationMs);
  setScrollTop(scroller, Math.max(0, savedAnchor.scrollTop));
}

function scrollElementToTop(
  element: HTMLElement,
  topOffset: number,
  scroller: HTMLElement | null
) {
  const viewportTop = getViewportTop(scroller);
  const rect = element.getBoundingClientRect();
  const currentScrollTop = getScrollTop(scroller);
  const nextScrollTop = currentScrollTop + rect.top - viewportTop - topOffset;
  setScrollTop(scroller, Math.max(0, nextScrollTop));
}

function getAppScroller() {
  return document.getElementById('App');
}

function getActiveScroller() {
  const scroller = getAppScroller();
  return scroller && elementUsesOwnScroll(scroller) ? scroller : null;
}

function elementUsesOwnScroll(element: HTMLElement) {
  const { overflowY } = window.getComputedStyle(element);
  return scrollableOverflowValues.has(overflowY);
}

function getViewportTop(scroller: HTMLElement | null) {
  return scroller ? scroller.getBoundingClientRect().top : 0;
}

function getViewportBottom(scroller: HTMLElement | null) {
  return scroller ? scroller.getBoundingClientRect().bottom : window.innerHeight;
}

function getScrollTop(scroller: HTMLElement | null) {
  const bodyRef = document.scrollingElement || document.documentElement;
  return scroller ? scroller.scrollTop : bodyRef?.scrollTop || 0;
}

// True only during setScrollTop's synchronous dispatch: those scroll events
// are the hook's own and fire before the caller can record the newly applied
// position, so the applied-position taint check must not classify them.
let applyingProgrammaticScroll = false;

function setScrollTop(scroller: HTMLElement | null, scrollTop: number) {
  const bodyRef = document.scrollingElement || document.documentElement;
  applyingProgrammaticScroll = true;
  try {
    if (scroller) {
      scroller.scrollTop = scrollTop;
      scroller.dispatchEvent(new Event('scroll'));
    } else if (bodyRef) {
      bodyRef.scrollTop = scrollTop;
      bodyRef.dispatchEvent(new Event('scroll'));
    }
    window.dispatchEvent(new Event('scroll'));
  } finally {
    applyingProgrammaticScroll = false;
  }
}
